import type { Properties } from "hast";
import { withMdastOrigin } from "~/components/mdast/provenance";

declare module "mdast" {
  /**
   * Citation `link` nodes carry their source reference on `data.reference`. It
   * is validated with {@link isReportReference} before use, so it is typed as
   * `unknown` rather than the concrete {@link ReportReference}.
   */
  interface LinkData {
    reference?: unknown;
  }
}

/**
 * Runtime validation + HAST-anchor mapping for the `data.reference` payload
 * carried by citation `link` nodes in a report section's `contentMdast`.
 *
 * `contentMdast` is loosely typed at the API boundary (§4.1), so references are
 * validated with a hand-written type guard — never an `as` cast — before any
 * field is read. Valid references map to the *exact* anchor markup v1 emits
 * (`source-map-link` etc.), so the existing citation grouping, source-label,
 * and click-delegation plumbing is reused unchanged (§6.2).
 */

/** Citation kinds the backend may emit on `data.reference.kind`. */
export type ReportReferenceKind = "page" | "section" | "bates";

/** Validated shape of a citation `link` node's `data.reference`. */
export type ReportReference = {
  kind: ReportReferenceKind;
  referenceType: string;
  title: string;
  pageId?: string;
  sectionId?: string;
  displayNumber?: number;
  absolutePageNumber?: number;
  batesPageId?: string;
  batesRange?: string;
};

/** Anchor class for page/source citations (identical to v1). */
export const SOURCE_MAP_LINK_CLASS = "source-map-link";
/** Anchor class for bates citations. */
export const BATES_LINK_CLASS = "bates-link";
/** Anchor class for section citations. */
export const SECTION_LINK_CLASS = "section-link";

const VALID_KINDS: ReadonlySet<string> = new Set<ReportReferenceKind>([
  "page",
  "section",
  "bates",
]);

/** Narrows an unknown value to a plain record without an unsafe cast. */
export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isOptionalString(value: unknown): value is string | undefined {
  return value === undefined || typeof value === "string";
}

function isOptionalNumber(value: unknown): value is number | undefined {
  return value === undefined || typeof value === "number";
}

/**
 * Type guard for a citation `data.reference`. Unknown/malformed references fail
 * and degrade to a plain, non-clickable link rather than crashing the render.
 */
export function isReportReference(value: unknown): value is ReportReference {
  if (!isRecord(value)) return false;
  const ref = value;

  if (typeof ref.kind !== "string" || !VALID_KINDS.has(ref.kind)) return false;
  if (typeof ref.referenceType !== "string") return false;
  if (typeof ref.title !== "string") return false;

  if (!isOptionalString(ref.pageId)) return false;
  if (!isOptionalString(ref.sectionId)) return false;
  if (!isOptionalNumber(ref.displayNumber)) return false;
  if (!isOptionalNumber(ref.absolutePageNumber)) return false;
  if (!isOptionalString(ref.batesPageId)) return false;
  if (!isOptionalString(ref.batesRange)) return false;

  return true;
}

/** Resolves the anchor class from the reference kind. */
function anchorClassForKind(kind: ReportReferenceKind): string {
  switch (kind) {
    case "bates":
      return BATES_LINK_CLASS;
    case "section":
      return SECTION_LINK_CLASS;
    case "page":
      return SOURCE_MAP_LINK_CLASS;
  }
}

/**
 * Maps a validated reference to the HAST anchor `properties` matching v1
 * exactly. Returns `null` when the reference cannot be an interactive citation
 * (a `bates` reference without the required `batesPageId` click target — §6.2),
 * in which case the caller renders non-interactive text.
 */
export function referenceToAnchorProperties(
  reference: ReportReference
): Properties | null {
  if (reference.kind === "bates" && reference.batesPageId === undefined) {
    return null;
  }

  const properties: Properties = withMdastOrigin({
    className: [anchorClassForKind(reference.kind)],
    href: "#",
    role: "button",
    // Title is emitted unconditionally when present so grouped citations keep
    // their source-document label even for short numeric labels (§6.2).
    title: reference.title,
  });

  if (reference.sectionId !== undefined) {
    properties.dataSectionId = reference.sectionId;
  }
  if (reference.pageId !== undefined) {
    properties.dataPageId = reference.pageId;
  }
  if (reference.displayNumber !== undefined) {
    properties.dataRefNumber = String(reference.displayNumber);
  }
  if (reference.batesPageId !== undefined) {
    properties.dataBatesPageId = reference.batesPageId;
  }
  if (reference.batesRange !== undefined) {
    properties.dataBatesRange = reference.batesRange;
  }

  return properties;
}
